/**
 * DataCoachApp.jsx
 * Data Coach main view — chat with the AI coach.
 *
 * Historical note (2026-09-18): this file used to drive three tabs
 * (dashboard / program / chat) backed by a training_plans/weeks/sessions
 * schema from the pre-pivot endurance-sport product. Dashboard and Program
 * were never reachable in production after the racket-only pivot — App.js
 * always mounts this with initialTab="chat" — so they were removed as dead
 * code in the 2026-09 refactor. Ks. docs_zenanalytics/00-PIVOT-2026-09.md.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { dataCoachApi } from './api';
import supabase from '../supabaseClient';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      '#060d1a',
  bg1:     '#0d1829',
  bg2:     '#111f35',
  bg3:     '#162540',
  border:  '#1a2d47',
  text:    '#f1f5f9',
  muted:   '#94a3b8',
  dim:     '#64748b',
  accent:  '#10b981',
  accentD: '#059669',
  blue:    '#38bdf8',
  yellow:  '#fbbf24',
  red:     '#f87171',
  font:    "'Inter', system-ui, sans-serif",
};



function mdToHtml(text) {
  const esc = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return esc
    .replace(/^### (.+)$/gm, '<strong style="display:block;margin:8px 0 2px;color:#f1f5f9">$1</strong>')
    .replace(/^## (.+)$/gm,  '<strong style="display:block;margin:8px 0 2px;color:#f1f5f9;font-size:15px">$1</strong>')
    .replace(/^# (.+)$/gm,   '<strong style="display:block;margin:8px 0 2px;color:#f1f5f9;font-size:15px">$1</strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g,    '<em>$1</em>')
    .replace(/`(.+?)`/g,      '<code style="background:#162540;padding:1px 5px;border-radius:3px;font-family:monospace;font-size:13px">$1</code>')
    .replace(/^[-*] (.+)$/gm, '<div style="padding-left:12px;margin:1px 0">• $1</div>')
    .replace(/^\d+\. (.+)$/gm,(_,p)=> `<div style="padding-left:12px;margin:1px 0">${p}</div>`)
    // Paragraph breaks (\n\n) must read as a real gap, not the same single
    // <br> as a plain line break — otherwise the whole answer looks like one
    // dense block even when the model does separate its paragraphs.
    .replace(/\n\n+/g, '<br><br>')
    .replace(/\n/g,   '<br>');
}

// Client-side CSV from a report's raw rows — no backend endpoint needed,
// same "build locally, trigger a download" pattern as the GDPR data export.
function downloadReportCsv(rows, label) {
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `zenanalytics-${(label || 'report').toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const WELCOME_MSG = {
  role: 'assistant',
  content: "Hi! I'm Data Coach — I read your game plans, results and notes and help you see the patterns in your own data. Ask me about a match, a pattern in your game, or how to prepare for your next opponent.",
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// Chat messages are capped at 700 characters (~45s of natural speech) — a
// tighter recording window than Game Plan fields' 90s, matched to the
// chat limit rather than borrowed from it. See
// docs_zenanalytics/24-CHAT-COST-MODEL-2026-10-28.md.
const CHAT_MIC_MAX_SECONDS = 45;

// Dictate-to-chat — same recording UX as Game Plan's MicButton (GamePlanTab.jsx)
// but its own component: different endpoint, shorter cap, and a truncation
// notice when speech runs past the 700-char message limit (the backend
// itself also truncates, so a dropped network response can't silently post
// something longer than the limit).
function ChatMicButton({ onText, onNotice }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(CHAT_MIC_MAX_SECONDS);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const autoStopRef = useRef(null);
  const tickRef = useRef(null);

  const clearTimers = () => {
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
  };

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        clearTimers();
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'recording.webm');
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          const res = await fetch(`${API}/data-coach/transcribe-chat`, {
            method: 'POST',
            headers: { ...(token && { Authorization: `Bearer ${token}` }) },
            body: fd,
          });
          if (res.status === 429) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.detail?.message || body?.detail || 'Daily dictation limit reached.');
          }
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (data.text) {
            onText(data.text);
            if (data.truncated) {
              onNotice(`Your voice message exceeded 700 characters — the first 700 characters were used.`);
            }
          } else {
            setError("Didn't catch anything — try again.");
          }
        } catch (e) {
          setError(e.message || "Couldn't transcribe — try again.");
        }
        setBusy(false);
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
      setSecondsLeft(CHAT_MIC_MAX_SECONDS);
      // Auto-stop at CHAT_MIC_MAX_SECONDS — same guard as Game Plan's mic,
      // just a shorter window matched to the chat message length limit.
      autoStopRef.current = setTimeout(() => stop(), CHAT_MIC_MAX_SECONDS * 1000);
      tickRef.current = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    } catch (e) {
      setError('Microphone access denied.');
    }
  };

  const stop = () => { recorderRef.current?.stop(); setRecording(false); };

  useEffect(() => () => clearTimers(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button type="button" onClick={recording ? stop : start} disabled={busy}
        title={recording ? 'Stop recording' : `Speech-to-text (max ${CHAT_MIC_MAX_SECONDS}s, 25/day)`}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          width: (recording || busy) ? 'auto' : 30, height: 30, minWidth: 30, flexShrink: 0,
          background: recording ? 'rgba(239,68,68,0.15)' : 'rgba(56,189,248,0.10)',
          border: `1px solid ${recording ? 'rgba(239,68,68,0.5)' : 'rgba(56,189,248,0.35)'}`,
          borderRadius: 15, color: recording ? '#fca5a5' : '#7dd3fc',
          fontSize: 11.5, fontWeight: 600, padding: (recording || busy) ? '4px 10px' : 0, cursor: busy ? 'default' : 'pointer',
          fontFamily: 'inherit', lineHeight: 1,
        }}>
        {busy ? (
          <>
            {/* Same explicit "Saving recording…" treatment as the Game Plan
                mic button (GamePlanTab.jsx) -- a bare "…" read as frozen
                during the transcription round-trip. Feedback 2026-11-02. */}
            <span aria-hidden="true" style={{
              display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
              border: '2px solid currentColor', borderTopColor: 'transparent',
              animation: 'zxMicSpin 0.7s linear infinite',
            }} />
            Saving recording…
          </>
        ) : recording ? (
          <>🔴 Recording speech… {secondsLeft}s</>
        ) : '🎤'}
      </button>
      {(recording || busy) && (
        <style>{'@keyframes zxMicSpin { to { transform: rotate(360deg); } }'}</style>
      )}
      {error && <span style={{ color: '#fca5a5', fontSize: 10.5 }}>{error}</span>}
    </span>
  );
}

function ChatTab({ userId, activeSport }) {
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput]   = useState('');
  const [sending, setSending] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [dictateNotice, setDictateNotice] = useState(null);
  const bottomRef = useRef(null);

  // Load persisted history on mount
  useEffect(() => {
    if (!userId) { setHistoryLoaded(true); return; }
    // Demo user: load pre-seeded conversation from data_coach_chat_history table
    const DEMO_USER_ID = 'f938b35d-537c-4398-8d0b-d32e9af9cbfc';
    if (userId === DEMO_USER_ID) {
      dataCoachApi.getChatHistory(userId)
        .then(({ messages: saved }) => {
          if (Array.isArray(saved) && saved.length > 0) {
            setMessages([WELCOME_MSG, ...saved]);
          }
        })
        .catch(e => console.error('[ChatTab] demo chat history load failed:', e))
        .finally(() => setHistoryLoaded(true));
      return;
    }
    // Regular users: load from profiles column
    supabase
      .from('profiles')
      .select('data_coach_chat_history')
      .eq('id', userId)
      .single()
      .then(({ data }) => {
        const saved = data?.data_coach_chat_history;
        if (Array.isArray(saved) && saved.length > 0) {
          setMessages([WELCOME_MSG, ...saved]);
        }
      })
      .catch(e => console.error('[ChatTab] chat history load failed:', e))
      .finally(() => setHistoryLoaded(true));
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save last 6 non-welcome messages to Supabase (fire-and-forget)
  const persistHistory = useCallback((msgs) => {
    if (!userId) return;
    const toSave = msgs.slice(1).slice(-6); // skip welcome msg, keep last 6
    supabase
      .from('profiles')
      .update({ data_coach_chat_history: toSave })
      .eq('id', userId)
      .then(({ error }) => { if (error) console.error('[chat persist]', error); });
  }, [userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput('');
    setSending(true);

    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);

    try {
      const history = newMessages.slice(1, -1).slice(-15).map(({ role, content }) => ({ role, content }));
      const { data: { session } } = await supabase.auth.getSession();
      const res = await dataCoachApi.chat(userId, msg, history, session?.access_token, activeSport);
      const updated = [...newMessages, {
        role: 'assistant', content: res.answer, model: res.model_used,
        reportData: res.report_data || null, reportLabel: res.report_label || null,
      }];
      setMessages(updated);
      persistHistory(updated);
    } catch (e) {
      console.error('[Data Coach chat error]', e.status, e.message, e);
      const isRateLimit = e.status === 429 && e.rateLimitInfo;
      const content = isRateLimit
        ? `⚠️ ${e.rateLimitInfo.message}`
        : `Something went wrong (${e.status || 'network'}: ${e.message || 'unknown'}). Please try again.`;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content,
        error: true,
        rateLimit: isRateLimit,
      }]);
    } finally {
      setSending(false);
    }
  };

  const QUICK = [
    'How did my last match go?',
    'What patterns do you see in my losses?',
    'What should I focus on next match?',
    'How is my serve trending?',
  ];

  return (
    <div style={{
      position: 'relative',
      height: 'calc(100dvh - 130px)',
      minHeight: '320px',
      display: 'flex', justifyContent: 'center',
    }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, background: C.bg }} />
    <div style={{
      position: 'relative', zIndex: 1,
      width: '100%', maxWidth: '760px',
      background: 'rgba(8,14,26,0.97)',
      // Full border (not just left/right) so the chat reads as its own
      // module, same as Home/Create & Manage's bordered cards — see
      // docs_zenanalytics/PRODUCT-GUIDELINES.md.
      border: '1.5px solid rgba(56,189,248,0.65)',
      display: 'flex', flexDirection: 'column',
      height: 'calc(100% - 12px)',
      marginTop: '12px',
      padding: '12px 16px 0',
    }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '8px' }}>
        {messages.map((m, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: '12px',
          }}>
            <div style={{
              maxWidth: '72%', padding: '10px 14px', borderRadius: '14px',
              background: m.role === 'user' ? `${C.accent}1a` : C.bg2,
              border: `1px solid ${m.role === 'user' ? `${C.accent}44` : C.border}`,
              color: m.error ? C.red : C.text, fontSize: '14px', lineHeight: 1.6,
              borderBottomRightRadius: m.role === 'user' ? '4px' : '14px',
              borderBottomLeftRadius:  m.role === 'user' ? '14px' : '4px',
            }}>
              <div style={{
                color: m.role === 'user' ? '#60a5fa' : '#34d399',
                fontWeight: 700, fontSize: 11, marginBottom: 5,
              }}>
                {m.role === 'user' ? 'You:' : 'Data Coach:'}
              </div>
              {m.role === 'user'
                ? m.content
                : <div dangerouslySetInnerHTML={{ __html: mdToHtml(m.content) }} />
              }
              {m.model && m.model.includes('sonnet') && (
                <div style={{ marginTop: '6px', color: C.dim, fontSize: '11px' }}>
                  Deep analysis
                </div>
              )}
              {m.reportData && m.reportData.length > 0 && (
                <button
                  onClick={() => downloadReportCsv(m.reportData, m.reportLabel)}
                  style={{
                    marginTop: '8px', background: 'none', border: `1px solid ${C.accent}66`,
                    borderRadius: '8px', color: C.accent, fontSize: '11.5px', fontWeight: 700,
                    padding: '6px 11px', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >⬇ Download CSV</button>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ display: 'flex', marginBottom: '12px' }}>
            <div style={{
              background: C.bg2, border: `1px solid ${C.border}`,
              borderRadius: '14px', borderBottomLeftRadius: '4px',
              padding: '10px 14px',
            }}>
              <div style={{ color: '#34d399', fontWeight: 700, fontSize: 11, marginBottom: 6 }}>
                Data Coach:
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                {[0, 1, 2].map(j => (
                  <div key={j} style={{
                    width: '7px', height: '7px', borderRadius: '50%', background: C.accent,
                    animation: `bounce 1.2s ${j * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {messages.length === 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
          {QUICK.map((q, i) => (
            <button key={i} onClick={() => setInput(q)} style={{
              background: C.bg2, border: `1px solid ${C.border}`, borderRadius: '20px',
              color: C.muted, fontSize: '12px', padding: '6px 12px', cursor: 'pointer',
            }}>
              {q}
            </button>
          ))}
        </div>
      )}

      {dictateNotice && (
        <div style={{
          background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 8, padding: '7px 11px', color: '#fbbf24', fontSize: 11.5,
          marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <span>{dictateNotice}</span>
          <button onClick={() => setDictateNotice(null)} style={{ background: 'none', border: 'none', color: '#fbbf24', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}

      <div style={{
        display: 'flex', gap: '8px', paddingTop: '10px', paddingBottom: '12px',
        borderTop: `1px solid ${C.border}`, alignItems: 'flex-end',
      }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ChatMicButton
              onText={t => setInput(i => (i ? `${i} ${t}` : t).slice(0, 700))}
              onNotice={setDictateNotice}
            />
          </div>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value.slice(0, 700))}
            placeholder="Ask your coach…"
            maxLength={700}
            rows={3}
            style={{
              width: '100%', background: C.bg2, border: `1px solid ${C.border}`,
              borderRadius: '10px', color: C.text, fontSize: '14px',
              padding: '10px 14px', outline: 'none', fontFamily: C.font,
              resize: 'none', lineHeight: '1.5', boxSizing: 'border-box',
              minHeight: '64px', maxHeight: '140px',
            }}
          />
          {input.length > 560 && (
            <div style={{ textAlign: 'right', fontSize: '11px', color: input.length >= 700 ? C.red : C.dim }}>
              {input.length}/700
            </div>
          )}
        </div>
        <button onClick={send} disabled={!input.trim() || sending} style={{
          background: input.trim() && !sending ? C.accent : C.bg2,
          border: 'none', borderRadius: '10px', color: '#fff',
          width: '44px', height: '44px', flexShrink: 0,
          cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '18px', transition: 'all 0.15s',
        }}>↑</button>
      </div>

      <style>{`
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        textarea::placeholder { color: ${C.dim}; }
      `}</style>
    </div>
    </div>
  );
}

// ── Main DataCoachApp ──────────────────────────────────────────────────────────

export default function DataCoachApp({ user, activeSport }) {
  const userId = user?.id;

  return (
    <div style={{ fontFamily: C.font, color: C.text }}>
      <ChatTab userId={userId} activeSport={activeSport} />
    </div>
  );
}
